import type { BetHistory, Card, CombinedRoundState } from './round';

export interface UserRoundHistory<T = CombinedRoundState> {
  id: string;
  userId: number;
  roundId: string;
  externalRoundId: string;
  gameId: string;
  gameName: string;
  roundOutcome: T;
  bets?: BetHistory[];
  outcome: string;
  outcomeAmount: number;
  outcomeAmountF: number;
  currencyCode: string;
  roundTime: string;
  version: number;
  createdAt: string;
  lastUpdatedAt: string;
}

export interface UserRoundHistoryItems {
  items: UserRoundHistory[];
  page: number;
  pageSize: number;
  totalPages: number;
  totalElements: number;
}

export interface RoundResult {
  roundId: string;
  winner: string;
  cancelled: boolean;
  winningCard: Card;
  winners: string[];
  winningCards: Card[];
  systemChoice?: string;
}

export interface GameRoundHistory {
  gameId: string;
  roundResults: RoundResult[];
}

export enum RuleLangV2 {
  ENG = 'ENG',
  HIN = 'HIN',
  BEN = 'BEN',
  TEL = 'TEL',
}

export interface GameRuleLangV2 {
  lang: RuleLangV2;
  layout: Record<string, unknown>;
}

export interface GameRulesV2 {
  id: string;
  gameId: string;
  rules: GameRuleLangV2[];
  lastUpdatedTime: string;
}

export const APP_LANGUAGE_TO_RULE_LANG: Record<string, RuleLangV2> = {
  en: RuleLangV2.ENG,
  hi: RuleLangV2.HIN,
  bn: RuleLangV2.BEN,
  te: RuleLangV2.TEL,
};

export function findRuleLayoutForLanguage(
  rules: GameRuleLangV2[],
  appLanguage: string
): Record<string, unknown> | undefined {
  const ruleLang = APP_LANGUAGE_TO_RULE_LANG[appLanguage] ?? RuleLangV2.ENG;
  return (
    rules.find((r) => r.lang === ruleLang)?.layout ??
    rules.find((r) => r.lang === RuleLangV2.ENG)?.layout ??
    rules[0]?.layout
  );
}

export interface MarketConfig {
  id: string;
  type: string;
  enabled: boolean;
  layEnabled: boolean;
  minStake: number | null;
  maxStake: number | null;
}

export interface CurrencyLimit {
  id: string;
  currencyCode: string;
  minAmount: number;
  maxAmount: number;
}

export type GameType =
  | 'DRAGON_TIGER_LION'
  | 'DRAGON_TIGER_20_20'
  | 'LUCKY_7'
  | 'ANDAR_BAHAR'
  | 'ANDAR_BAHAR_2'
  | 'TEEN_PATTI_20_20'
  | 'TEEN_PATTI_ONE_DAY'
  | 'AMAR_AKBAR_ANTHONY'
  | 'THIRTY_TWO_CARDS_A'
  | 'CASINO_WAR'
  | 'BOLLYWOOD_CASINO'
  | 'LUCKY_5'
  | 'LUCKY_7B'
  | 'PAPER_TOSS'
  | 'ROCK_PAPER_SCISSORS';

export type AmarAkbarAnthonyWinnerType = 'AMAR' | 'AKBAR' | 'ANTHONY';

export interface GameConfig {
  id: string | null;
  gameId: string;
  displayName: string;
  operatorId: string;
  available: boolean;
  type: GameType;
  videoUrl: string;
  mainMarket: MarketConfig;
  mainMarkets: MarketConfig[];
  sideMarkets: MarketConfig[];
  betTimerLimit: number;
  maxBet: number;
  maxWin: number | null;
  imageUrl: string | null;
  tags: string[] | null;
  createdDate: string | null;
  lastUpdatedDate: string | null;
  version: number;
  currencyLimits: CurrencyLimit[];
  videoEnabled: boolean;
  layEnabled: boolean;
}

export interface HistoryGameFilter {
  gameName: string;
  gameId: string;
}

export interface Chips {
  id: string;
  currencyCode: string;
  chips: Array<{ value: number }>;
}

export interface InactivityConfig {
  inactivityTimeout: number;
  showLoseMessage: boolean;
  type: GameType;
  lobbyEnabled: boolean;
}

export interface GamePreferences {
  userId: number;
  gameId: string;
  stakes: number[];
}
